// Supplier-order items section — visual mirror of OrderProductsSection.
// Same operational fields as the customer page, with product identity,
// dimensions and the catalog action grouped into one readable product column.
// Supplier purchase columns are shown only when the caller passes supplier
// purchase-price visibility.
//
// Column layout is elastic by design: the table must fit the card WITHOUT a
// horizontal scrollbar at ordinary desktop widths, so the price column carries
// its own currency (no separate currency column) and the product column takes
// the slack. See ITEM_COLUMN_WIDTHS below before adding a column here.
//
// Live edit via the existing PUT /supplier-orders/:id endpoint; the backend
// replaces items wholesale when `items` is present in the payload
// (see supplierOrderService.buildSupplierOrderPayload).
//
// The shared quantity editor owns the full dimensional pair here as well as in
// the wizard: the operator may change the entered unit, conversion target and
// purchase-price unit independently. New rows start in pieces only as an empty
// form default; it is never a catalog or warehouse lock.
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Tag,
  Table,
  Tooltip,
  Typography,
  App,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatCurrencyUnitPrice } from '../../../utils/currency';
import { formatDate } from '../../../utils/dateUtils';
import { formatProductDimensions } from '../../../utils/productDimensions';
import { MAX_AMOUNT, MAX_DIMENSION, MIN_QUANTITY, SupplierOrderDto, SupplierOrderItemDto } from '../../../dto/supplierOrder';
import { SupplierOrderItemPayload, updateSupplierOrder } from '../api/supplierOrdersApi';
import { getSupplierOrderDisplayNumber } from '../../../dto/supplierOrder';
import { useFeedback } from '../../../utils/feedback';
import { getSupplierOrderErrorMessage } from '../supplierOrderErrorMessages';
import { buildSupplierOrderItemsUpdateData } from '../supplierOrderDetailEditUtils';
import { formatCommercialPriceUnit } from '../commercialPriceUnit';
import CommercialQuantityEditor from '../components/CommercialQuantityEditor';
import { VariantPicker } from '../../../components/inventory';
import { ProductVariant } from '../../../types/pz';
import { ORDER_CURRENCIES } from '../currencies';
import {
  buildSupplierOrderVariantAutofill,
  isDefaultPurchaseAutofillApplied,
} from '../supplierOrderVariantUtils';
import {
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../orderDetailLayout';
import { getOrderProductDisplayName } from '../orderProductDisplay';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import { UNIT_PRICE_INPUT_PROPS, calculateLineValue } from '../../../utils/money';
import { formatUnit } from '../../../utils/units';
import { buildQuantitySetFromPair, quantitySetToFields } from '../../../utils/commercialQuantity';
import './SupplierOrderItemsSection.css';

const MAX_ITEMS = 200;
const PRODUCT_NAME_TEXTAREA_AUTO_SIZE = { minRows: 2, maxRows: 2 };
// These are a MINIMUM width budget, not a fixed layout. `scroll.x` is their sum
// and rc-table renders the table as `width: <sum>; min-width: 100%`, so the
// product column — deliberately declared WITHOUT a width — absorbs every extra
// pixel the card gives us. Keep the sum small enough that the whole table fits
// the supplier-order card at ordinary desktop widths (~900px of card body,
// i.e. a 1280px viewport with the sidebar open) without a horizontal scrollbar;
// the scrollbar then only appears on genuinely narrow screens.
// `product` is the minimum share reserved for the (width-less) product column.
const ITEM_COLUMN_WIDTHS = {
  // Fits the 170px catalog picker plus cell padding in edit mode, and keeps
  // the edit-mode sum (which additionally carries `actions`) under the
  // read-mode card width so entering edit never introduces the scrollbar.
  product: 200,
  // Above the 260px container-query threshold in CommercialQuantityEditor.css
  // (minus ~16px cell padding), so the physical -> commercial chain keeps its
  // readable side-by-side layout instead of stacking in the narrowest case.
  quantity: 280,
  unitCost: 180,
  total: 180,
  actions: 44,
} as const;
interface EditableItem {
  key: string;
  id?: string | null;
  productVariantId?: string | null;
  name: string;
  displayName: string;
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  unit: SupplierOrderItemPayload['unit'];
  purchaseQuantity: number;
  purchaseUnit: SupplierOrderItemPayload['unit'];
  conversionQuantity?: number;
  conversionUnit?: 'pcs' | 'm2' | 'm3' | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  currency?: string;
  unitCost?: number;
  totalCostPLN?: number;
  exchangeRateToPLN?: number;
  exchangeRateToPLNSource?: string;
  exchangeRateToPLNTableNumber?: string;
  exchangeRateToPLNEffectiveDate?: string;
  defaultPurchasePrice?: number | null;
  defaultPurchaseCurrency?: string | null;
  description?: string | null;
}

interface SupplierOrderItemsSectionProps {
  supplierOrder: SupplierOrderDto;
  canEdit: boolean;
  canViewSupplierPurchasePrices: boolean;
  purchasePricesIncomplete?: boolean;
  onSaved: () => Promise<unknown>;
  onOpenTransports?: () => void;
}

type QuantityChangeDecision = 'SUPPLIER_SHORTFALL' | 'CUSTOMER_REDUCTION' | 'TRANSPORT_PARTIAL';

interface QuantityConflictAllocation {
  id?: string | null;
  orderId: string;
  orderNumber?: string | null;
  customerName?: string | null;
  allocatedQuantity: number;
  allocatedUnit?: string | null;
  proposedAllocatedQuantity: number;
  customerQuantity?: number | null;
  customerUnit?: string | null;
  proposedCustomerQuantity?: number | null;
}

interface QuantityConflict {
  supplierOrderItemId: string;
  name: string;
  unit: string;
  currentQuantity: number;
  nextQuantity: number;
  shortageQuantity: number;
  allocations: QuantityConflictAllocation[];
}

const toEditableRows = (items: SupplierOrderItemDto[] = []): EditableItem[] =>
  items.map((item, index) => ({
    key: item.id || `row-${index}`,
    id: item.id || null,
    productVariantId: item.productVariantId ?? null,
    name: item.name,
    displayName: getOrderProductDisplayName(item, item.name),
    quantity: item.quantity,
    quantityPcs: item.quantityPcs ?? null,
    quantityM2: item.quantityM2 ?? null,
    quantityM3: item.quantityM3 ?? null,
    unit: (item.unit as SupplierOrderItemPayload['unit']) || 'pcs',
    purchaseQuantity: item.purchaseQuantity ?? item.quantity,
    purchaseUnit: (item.purchaseUnit as SupplierOrderItemPayload['unit']) || (item.unit as SupplierOrderItemPayload['unit']) || 'pcs',
    conversionQuantity: item.conversionQuantity,
    conversionUnit: item.conversionUnit ?? null,
    width: item.width ?? null,
    height: item.height ?? null,
    length: item.length ?? null,
    thickness: item.thickness ?? null,
    productCategoryName: item.productCategoryName ?? null,
    productCategoryColor: item.productCategoryColor ?? null,
    productCategoryDimensionDisplayProfile: item.productCategoryDimensionDisplayProfile ?? null,
    currency: item.currency ?? undefined,
    unitCost: item.unitCost,
    totalCostPLN: item.totalCostPLN,
    exchangeRateToPLN: item.exchangeRateToPLN,
    exchangeRateToPLNSource: item.exchangeRateToPLNSource,
    exchangeRateToPLNTableNumber: item.exchangeRateToPLNTableNumber,
    exchangeRateToPLNEffectiveDate: item.exchangeRateToPLNEffectiveDate,
    description: item.description ?? null,
  }));

export const SupplierOrderItemsSection: React.FC<SupplierOrderItemsSectionProps> = ({
  supplierOrder,
  canEdit,
  canViewSupplierPurchasePrices,
  purchasePricesIncomplete = false,
  onSaved,
  onOpenTransports,
}) => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const feedback = useFeedback();
  const { modal } = App.useApp();
  const displayOrderNumber = getSupplierOrderDisplayNumber(supplierOrder);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<EditableItem[]>(() => toEditableRows(supplierOrder.items));
  const [quantityConflicts, setQuantityConflicts] = useState<QuantityConflict[]>([]);
  const [pendingItemsPayload, setPendingItemsPayload] = useState<SupplierOrderItemPayload[] | null>(null);
  const [quantityDecision, setQuantityDecision] = useState<QuantityChangeDecision>('SUPPLIER_SHORTFALL');

  useEffect(() => {
    if (!editing) {
      setRows(toEditableRows(supplierOrder.items));
    }
    // Refresh the local buffer only when the underlying items change AND we
    // are not currently editing — protects the in-progress edit from being
    // clobbered by a parallel refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierOrder.items]);

  const startEditing = () => {
    setRows(toEditableRows(supplierOrder.items));
    if ((supplierOrder.items?.length || 0) === 0) {
      setRows([{ key: 'row-0', name: '', displayName: '', quantity: 1, unit: 'pcs', purchaseQuantity: 1, purchaseUnit: 'pcs', unitCost: 0 }]);
    }
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setRows(toEditableRows(supplierOrder.items));
  };

  const updateRow = (key: string, patch: Partial<EditableItem>) => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const handleVariantChange = (key: string, variantId: string | undefined, variant?: ProductVariant) => {
    const currentRow = rows.find((row) => row.key === key);
    const patch: Partial<EditableItem> = {
      productVariantId: variantId || null,
      width: null,
      height: null,
      length: null,
      thickness: null,
      productCategoryName: null,
      productCategoryColor: null,
      productCategoryDimensionDisplayProfile: null,
      defaultPurchasePrice: null,
      defaultPurchaseCurrency: null,
      purchaseQuantity: currentRow?.quantity || 1,
      purchaseUnit: currentRow?.unit || 'pcs',
      conversionQuantity: undefined,
      conversionUnit: null,
    };

    if (variant) {
      const autofill = buildSupplierOrderVariantAutofill(variant, currentRow?.unit || 'pcs');
      patch.name = autofill.name;
      patch.displayName = getOrderProductDisplayName({
        name: autofill.name,
        productVariantId: variant.id,
        productCode: variant.product?.code,
        productEan: variant.ean,
      }, autofill.name);
      patch.unit = autofill.unit;
      patch.purchaseUnit = autofill.unit;
      patch.width = autofill.width ?? null;
      patch.height = autofill.height ?? null;
      patch.length = autofill.length ?? null;
      patch.thickness = autofill.thickness ?? null;
      patch.productCategoryName = autofill.productCategoryName ?? null;
      patch.productCategoryColor = autofill.productCategoryColor ?? null;
      patch.productCategoryDimensionDisplayProfile = autofill.productCategoryDimensionDisplayProfile ?? null;
      if (canViewSupplierPurchasePrices && autofill.defaultPurchasePrice != null) {
        patch.unitCost = autofill.defaultPurchasePrice;
        if (
          autofill.defaultPurchaseCurrency
          && ORDER_CURRENCIES.includes(autofill.defaultPurchaseCurrency as typeof ORDER_CURRENCIES[number])
        ) {
          patch.currency = autofill.defaultPurchaseCurrency;
        }
      }
      patch.defaultPurchasePrice = autofill.defaultPurchasePrice ?? null;
      patch.defaultPurchaseCurrency = autofill.defaultPurchaseCurrency ?? null;
    }

    updateRow(key, patch);
  };

  const addRow = () => {
    setRows((current) => [
      ...current,
      {
        key: `row-${current.length}-${Math.random().toString(36).slice(2, 6)}`,
        name: '',
        displayName: '',
        quantity: 1,
        unit: 'pcs',
        purchaseQuantity: 1,
        purchaseUnit: 'pcs',
        currency: supplierOrder.currency || 'PLN',
        unitCost: 0,
      },
    ]);
  };

  const removeRow = (key: string) => {
    setRows((current) => (current.length > 1 ? current.filter((row) => row.key !== key) : current));
  };

  const validate = (): { ok: true; payload: SupplierOrderItemPayload[] } | { ok: false; reason: string } => {
    if (rows.length > MAX_ITEMS) {
      return { ok: false, reason: t('orders:supplierOrders.detail.tooManyItems', { max: MAX_ITEMS }) };
    }
    const payload: SupplierOrderItemPayload[] = [];
    for (const row of rows) {
      const name = row.name?.trim();
      if (!name) return { ok: false, reason: t('orders:supplierOrders.detail.itemNameRequired') };
      const qty = Number(row.quantity);
      if (!Number.isFinite(qty) || qty <= 0) return { ok: false, reason: t('orders:supplierOrders.detail.itemQuantityRequired') };
      // Same floor as supplierOrderQuantityRule (precision={3} input step).
      if (qty < MIN_QUANTITY || qty > MAX_AMOUNT) return { ok: false, reason: t('orders:supplierOrders.form.quantityPositiveInvalid', { max: MAX_AMOUNT }) };
      const cost = Number(row.unitCost ?? 0);
      const purchaseQuantity = Number(row.purchaseQuantity ?? row.quantity);
      if (!Number.isFinite(purchaseQuantity) || purchaseQuantity <= 0 || purchaseQuantity > MAX_AMOUNT) {
        return { ok: false, reason: t('orders:supplierOrders.detail.itemQuantityRequired') };
      }
      if (!Number.isFinite(cost) || cost < 0) return { ok: false, reason: t('orders:supplierOrders.detail.itemUnitCostRequired') };
      if (cost > MAX_AMOUNT) return { ok: false, reason: t('orders:supplierOrders.form.amountInvalid', { max: MAX_AMOUNT }) };
      const currency = row.currency || supplierOrder.currency || 'PLN';
      if (!ORDER_CURRENCIES.includes(currency as typeof ORDER_CURRENCIES[number])) {
        return { ok: false, reason: t('orders:modal.validation.currencyRequired') };
      }
      for (const dim of [row.length, row.width, row.height, row.thickness]) {
        if (dim != null && (!Number.isFinite(Number(dim)) || Number(dim) < 0 || Number(dim) > MAX_DIMENSION)) {
          return { ok: false, reason: t('orders:supplierOrders.form.dimensionInvalid', { max: MAX_DIMENSION }) };
        }
      }
      payload.push({
        id: row.id || null,
        name,
        quantity: qty,
        quantityPcs: row.quantityPcs,
        quantityM2: row.quantityM2,
        quantityM3: row.quantityM3,
        unit: row.unit || 'pcs',
        purchaseQuantity,
        purchaseUnit: row.purchaseUnit || row.unit || 'pcs',
        conversionQuantity: row.conversionQuantity,
        conversionUnit: row.conversionUnit ?? null,
        width: row.width ?? null,
        height: row.height ?? null,
        length: row.length ?? null,
        thickness: row.thickness ?? null,
        currency,
        unitCost: cost,
        productVariantId: row.productVariantId || null,
        description: row.description || null,
      });
    }
    return { ok: true, payload };
  };

  const saveItems = async (
    payload: SupplierOrderItemPayload[],
    quantityChangeReason?: Exclude<QuantityChangeDecision, 'TRANSPORT_PARTIAL'>
  ) => {
    return updateSupplierOrder(supplierOrder.id, {
      ...buildSupplierOrderItemsUpdateData(supplierOrder, payload),
      ...(quantityChangeReason ? { quantityChangeReason } : {}),
    }, { modal, t });
  };

  const handleSave = async () => {
    if (!supplierOrder.id) return;
    const result = validate();
    if (!result.ok) {
      feedback.error(result.reason);
      return;
    }
    setSaving(true);
    try {
      if (!await saveItems(result.payload)) return;
      feedback.success(t('orders:supplierOrders.detail.updateSuccess', { orderNumber: displayOrderNumber }));
      setEditing(false);
      await onSaved();
    } catch (error: any) {
      if (error?.response?.data?.code === 'SUPPLIER_ORDER_QUANTITY_DECISION_REQUIRED') {
        setPendingItemsPayload(result.payload);
        setQuantityConflicts(error.response.data?.details?.conflicts || []);
        setQuantityDecision('SUPPLIER_SHORTFALL');
        return;
      }
      if (error?.response?.data?.code === 'SUPPLIER_ORDER_PZ_REDUCTION_BLOCKED') {
        feedback.error(t('orders:supplierOrders.detail.quantityChange.pzBlocked'));
        return;
      }
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.updateError', {
        orderNumber: displayOrderNumber,
      }));
    } finally {
      setSaving(false);
    }
  };

  const closeQuantityDecision = () => {
    setQuantityConflicts([]);
    setPendingItemsPayload(null);
  };

  const confirmQuantityDecision = async () => {
    if (quantityDecision === 'TRANSPORT_PARTIAL') {
      closeQuantityDecision();
      cancelEditing();
      onOpenTransports?.();
      return;
    }
    if (!pendingItemsPayload) return;
    setSaving(true);
    try {
      await saveItems(pendingItemsPayload, quantityDecision);
      feedback.success(t('orders:supplierOrders.detail.updateSuccess', { orderNumber: displayOrderNumber }));
      closeQuantityDecision();
      setEditing(false);
      await onSaved();
    } catch (error: any) {
      if (error?.response?.data?.code === 'SUPPLIER_ORDER_PZ_REDUCTION_BLOCKED') {
        feedback.error(t('orders:supplierOrders.detail.quantityChange.pzBlocked'));
      } else {
        feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.updateError', {
          orderNumber: displayOrderNumber,
        }));
      }
    } finally {
      setSaving(false);
    }
  };

  const dataSource = editing ? rows : toEditableRows(supplierOrder.items);
  const canViewCosts = canViewSupplierPurchasePrices;
  const trailingColumnWidth = editing ? ITEM_COLUMN_WIDTHS.actions : 0;
  const tableScrollWidth = ITEM_COLUMN_WIDTHS.product
    + ITEM_COLUMN_WIDTHS.quantity
    + (canViewCosts ? ITEM_COLUMN_WIDTHS.unitCost + ITEM_COLUMN_WIDTHS.total : 0)
    + trailingColumnWidth;

  const formatPurchaseAmount = (amount: number, currency?: string) => (
    currency ? formatCurrency(amount, currency) : '-'
  );
  // Unit prices carry 4dp (owner decision 2026-09-11); amounts stay 2dp.
  const formatPurchaseUnitPrice = (amount: number, currency?: string) => (
    currency ? formatCurrencyUnitPrice(amount, currency, i18n.language) : '-'
  );

  const renderDefaultPurchaseHint = (record: EditableItem) => {
    if (!isDefaultPurchaseAutofillApplied(record)) return null;
    return (
      <span style={{ color: '#64748b', fontSize: 12, lineHeight: 1.35 }}>
        {t('orders:supplierOrders.form.defaultPurchaseApplied', {
          amount: formatPurchaseUnitPrice(record.defaultPurchasePrice || 0, record.defaultPurchaseCurrency || 'PLN'),
        })}
      </span>
    );
  };

  const renderLinePlnContext = (record: EditableItem) => {
    const lineCurrency = record.currency || supplierOrder.currency || 'PLN';
    if (editing || lineCurrency === 'PLN' || record.totalCostPLN == null) return null;

    const meta = [
      record.exchangeRateToPLN
        ? `${t('orders:supplierOrders.detail.lineExchangeRate')}: ${record.exchangeRateToPLN}`
        : null,
      record.exchangeRateToPLNEffectiveDate
        ? `${t('orders:supplierOrders.detail.exchangeRateEffectiveDate')}: ${formatDate(record.exchangeRateToPLNEffectiveDate)}`
        : null,
      record.exchangeRateToPLNSource || null,
      record.exchangeRateToPLNTableNumber
        ? `${t('orders:supplierOrders.detail.exchangeRateTable')}: ${record.exchangeRateToPLNTableNumber}`
        : null,
    ].filter(Boolean);

    return (
      <Space direction="vertical" size={0}>
        <span style={{ color: '#475569', fontSize: 12 }}>
          {t('orders:supplierOrders.detail.lineValuePLN')}: {formatPurchaseAmount(record.totalCostPLN, 'PLN')}
        </span>
        {meta.length > 0 && (
          <span style={{ color: '#64748b', fontSize: 12 }}>{meta.join(' · ')}</span>
        )}
      </Space>
    );
  };

  const columns: TableColumnsType<EditableItem> = [
    // Product identity and catalog action stay together. The compact picker is
    // a secondary action instead of a competing column.
    {
      title: t('orders:orderDetail.product'),
      dataIndex: 'name',
      key: 'name',
      // No `width` on purpose: this is the elastic column that soaks up
      // whatever the card is wider than `tableScrollWidth` (see
      // ITEM_COLUMN_WIDTHS), which is what keeps the table scrollbar-free.
      fixed: 'left',
      render: (_value: string, record) => {
        const dimensions = formatProductDimensions(record);

        if (editing) {
          return (
            <div className="supplier-order-items__product-editor">
              <Tooltip title={record.displayName || undefined} trigger={['hover', 'focus']}>
                <Input.TextArea
                  autoSize={PRODUCT_NAME_TEXTAREA_AUTO_SIZE}
                  value={record.displayName}
                  onChange={(event) => updateRow(record.key, {
                    name: event.target.value,
                    displayName: event.target.value,
                  })}
                  placeholder={t('orders:orderDetail.productName')}
                  aria-label={t('orders:orderDetail.productName')}
                  className="supplier-order-items__product-name-input"
                />
              </Tooltip>
              <div className="supplier-order-items__product-meta">
                <div className="supplier-order-items__catalog-picker">
                  <VariantPicker
                    size="small"
                    purchasePriceScope="supplier-order"
                    value={record.productVariantId || undefined}
                    showInventory={false}
                    triggerLabel={record.productVariantId
                      ? t('orders:orderDetail.variantPickerChange')
                      : t('orders:supplierOrders.form.selectProductVariant')}
                    placeholder={t('orders:supplierOrders.form.selectProductVariant')}
                    onChange={(variantId, variant) => handleVariantChange(record.key, variantId, variant)}
                  />
                </div>
                {dimensions && (
                  <span className="supplier-order-items__dimensions">
                    {dimensions}
                  </span>
                )}
              </div>
            </div>
          );
        }

        return (
          <div className="supplier-order-items__product-readonly">
            <Tooltip title={record.displayName || undefined} trigger={['hover', 'focus']}>
              <span
                className="supplier-order-items__product-name"
                tabIndex={record.displayName ? 0 : undefined}
                aria-label={record.displayName || undefined}
              >
                {record.displayName}
              </span>
            </Tooltip>
            <div className="supplier-order-items__product-meta">
              {record.productVariantId ? (
                <Tag color="blue" bordered={false}>
                  {t('orders:supplierOrders.detail.variantLinked')}
                </Tag>
              ) : (
                <span className="supplier-order-items__variant-missing">
                  {t('orders:supplierOrders.detail.variantMissing')}
                </span>
              )}
              {dimensions && (
                <span className="supplier-order-items__dimensions">
                  {dimensions}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    // 2. Ilość — unit suffix preserved as a small grey tail (not a separate column)
    {
      title: t('orders:orderDetail.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: ITEM_COLUMN_WIDTHS.quantity,
      render: (value: number, record) => {
        if (editing) {
          return (
            <CommercialQuantityEditor
              compact
              maxPhysicalQuantity={MAX_AMOUNT}
              maxPricingQuantity={MAX_AMOUNT}
              width={record.width}
              length={record.length}
              height={record.height}
              thickness={record.thickness}
              physicalUnit={(record.unit || 'pcs') as import('../../../utils/commercialQuantity').QuantityUnit}
              value={{
                physicalQuantity: record.quantity,
                physicalUnit: (record.unit || 'pcs') as 'pcs' | 'm2' | 'm3',
                pricingQuantity: record.purchaseQuantity ?? record.quantity,
                pricingUnit: (record.purchaseUnit || record.unit || 'pcs') as 'pcs' | 'm2' | 'm3',
                conversionQuantity: record.conversionQuantity,
                conversionUnit: record.conversionUnit ?? null,
                quantityPcs: record.quantityPcs ?? null,
                quantityM2: record.quantityM2 ?? null,
                quantityM3: record.quantityM3 ?? null,
              }}
              onChange={(next) => {
                const nextQuantitySet = buildQuantitySetFromPair(next, record);
                updateRow(record.key, {
                  quantity: next.physicalQuantity,
                  unit: next.physicalUnit,
                  purchaseQuantity: next.pricingQuantity,
                  purchaseUnit: next.pricingUnit,
                  conversionQuantity: next.conversionQuantity,
                  conversionUnit: next.conversionUnit ?? null,
                  ...(nextQuantitySet ? quantitySetToFields(nextQuantitySet) : {}),
                });
              }}
            />
          );
        }
        return (
          <CommercialQuantityEditor
            compact
            disabled
            maxPhysicalQuantity={MAX_AMOUNT}
            maxPricingQuantity={MAX_AMOUNT}
            width={record.width}
            length={record.length}
            height={record.height}
            thickness={record.thickness}
            value={{
              physicalQuantity: value,
              physicalUnit: (record.unit || 'pcs') as 'pcs' | 'm2' | 'm3',
              pricingQuantity: record.purchaseQuantity ?? value,
              pricingUnit: (record.purchaseUnit || record.unit || 'pcs') as 'pcs' | 'm2' | 'm3',
              conversionQuantity: record.conversionQuantity,
              conversionUnit: record.conversionUnit ?? null,
              quantityPcs: record.quantityPcs ?? null,
              quantityM2: record.quantityM2 ?? null,
              quantityM3: record.quantityM3 ?? null,
            }}
            onChange={() => undefined}
          />
        );
      },
    },
    // 3. Supplier purchase unit cost — price and its currency share ONE column.
    // A separate "Waluta ceny zakupu" column cost ~112px to repeat information
    // the formatted amount already carries ("€4,00" / "4,00 zł"); the per-line
    // currency is still fully editable, just stacked under the price input.
    ...(canViewCosts
      ? [
          {
            title: t('orders:supplierOrders.form.unitCostWithPurchaseUnit'),
            dataIndex: 'unitCost',
            key: 'unitCost',
            width: ITEM_COLUMN_WIDTHS.unitCost,
            render: (value: number | undefined, record: EditableItem) =>
              editing ? (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <InputNumber
                    {...UNIT_PRICE_INPUT_PROPS}
                    {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                    addonAfter={`/ ${formatCommercialPriceUnit('purchase', record, i18n.language)}`}
                    value={record.unitCost}
                    aria-label={t('orders:supplierOrders.form.unitCostWithPurchaseUnit')}
                    onChange={(v) => updateRow(record.key, { unitCost: Number(v ?? 0) })}
                    style={{ width: '100%' }}
                  />
                  <Tooltip title={t('orders:supplierOrders.form.purchaseCurrencyHelp')}>
                    <Select
                      size="small"
                      value={record.currency || supplierOrder.currency || 'PLN'}
                      onChange={(currency) => updateRow(record.key, { currency })}
                      style={{ width: 92 }}
                      aria-label={t('orders:supplierOrders.form.purchaseCurrency')}
                    >
                      {ORDER_CURRENCIES.map((currency) => (
                        <Select.Option key={currency} value={currency}>
                          {currency}
                        </Select.Option>
                      ))}
                    </Select>
                  </Tooltip>
                  {renderDefaultPurchaseHint(record)}
                </Space>
              ) : (
                value === undefined ? '-' : (
                  <Space size={4} wrap>
                    <span>{formatPurchaseUnitPrice(value, record.currency || supplierOrder.currency)}</span>
                    <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                      / {formatCommercialPriceUnit('purchase', record, i18n.language)}
                    </span>
                  </Space>
                )
              ),
          },
        ]
      : []),
    // 4. Supplier purchase total.
    ...(canViewCosts
      ? [
          {
            title: t('orders:wizard.summary.purchaseLineValue'),
            key: 'totalPrice',
            width: ITEM_COLUMN_WIDTHS.total,
            render: (_value: unknown, record: EditableItem) =>
              record.unitCost === undefined
                ? '-'
                : (
                  <Space direction="vertical" size={0}>
                    <span>{formatPurchaseAmount(calculateLineValue(record.purchaseQuantity ?? record.quantity, record.unitCost), record.currency || supplierOrder.currency)}</span>
                    {renderLinePlnContext(record)}
                  </Space>
                ),
          },
        ]
      : []),
    ...(editing
      ? [
          {
            title: '',
            key: 'actions',
            width: ITEM_COLUMN_WIDTHS.actions,
            fixed: 'right' as const,
            // Hide (not disable) the delete control on the last remaining row —
            // mirrors OrderProductsSection's behavior so the two pages render
            // 1:1 in edit mode.
            render: (_value: unknown, record: EditableItem) =>
              dataSource.length > 1 ? (
                <Tooltip title={t('common:delete')}>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeRow(record.key)}
                  />
                </Tooltip>
              ) : null,
          },
        ]
      : []),
  ];

  // Derived, never hardcoded: the leading summary cell must span exactly the
  // columns before "Wartość zakupu pozycji", and that count changes with the
  // permission-gated / edit-only columns above.
  const totalColumnIndex = columns.findIndex((column) => column.key === 'totalPrice');

  const totalCostsByCurrency = dataSource.reduce<Record<string, number>>((totals, row) => {
    const currency = row.currency || supplierOrder.currency || 'PLN';
    totals[currency] = (totals[currency] || 0) + (row.purchaseQuantity ?? row.quantity) * (row.unitCost ?? 0);
    return totals;
  }, {});
  const totalCostEntries = Object.entries(totalCostsByCurrency);
  const totalCostPLN = dataSource.reduce((sum, row) => {
    if (row.totalCostPLN != null) return sum + Number(row.totalCostPLN || 0);
    const currency = row.currency || supplierOrder.currency || 'PLN';
    return currency === 'PLN' ? sum + (row.purchaseQuantity ?? row.quantity) * (row.unitCost ?? 0) : sum;
  }, 0);

  return (
    <Card
      title={renderSectionTitle(t('orders:supplierOrders.detail.items'))}
      style={{ ...SECTION_CARD_STYLE, marginBottom: 16 }}
      headStyle={SECTION_CARD_HEAD_STYLE}
      bodyStyle={SECTION_CARD_BODY_STYLE}
      extra={
        canEdit ? (
          editing ? (
            <Space>
              <Button type="primary" size="small" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                {t('common:save')}
              </Button>
              <Button size="small" icon={<CloseOutlined />} disabled={saving} onClick={cancelEditing}>
                {t('common:cancel')}
              </Button>
            </Space>
          ) : (
            <Button size="small" icon={<EditOutlined />} onClick={startEditing}>
              {t('orders:supplierOrders.detail.editItems')}
            </Button>
          )
        ) : null
      }
    >
      {canViewSupplierPurchasePrices && purchasePricesIncomplete && (
        <Alert
          type="warning"
          showIcon
          message={t('orders:supplierOrders.detail.purchasePricesIncompleteTitle')}
          description={t('orders:supplierOrders.detail.purchasePricesIncompleteDescription')}
          action={
            canEdit && !editing ? (
              <Button size="small" type="primary" onClick={startEditing}>
                {t('orders:supplierOrders.detail.editItems')}
              </Button>
            ) : undefined
          }
          style={{ marginBottom: 16 }}
        />
      )}
      <div className="supplier-order-items__table-shell" data-testid="supplier-order-items-table-shell">
        <Table<EditableItem>
          rowKey="key"
          size="middle"
          pagination={false}
          dataSource={dataSource}
          columns={columns}
          tableLayout="fixed"
          scroll={{ x: tableScrollWidth }}
          locale={{ emptyText: t('orders:supplierOrders.detail.noItems') }}
          summary={() => canViewCosts ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={totalColumnIndex}>
                <strong>{t('orders:orderDetail.total')}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={totalColumnIndex}>
                <Space direction="vertical" size={0}>
                  {totalCostEntries.map(([currency, amount]) => (
                    <strong key={currency}>{formatPurchaseAmount(amount, currency)}</strong>
                  ))}
                  {!editing && totalCostPLN > 0 && (
                    <span style={{ color: '#475569', fontSize: 12 }}>
                      {t('orders:supplierOrders.detail.totalValuePLN')}: {formatPurchaseAmount(totalCostPLN, 'PLN')}
                    </span>
                  )}
                </Space>
              </Table.Summary.Cell>
              {editing && <Table.Summary.Cell index={totalColumnIndex + 1} />}
            </Table.Summary.Row>
          ) : null}
        />
      </div>
      {editing && (
        <div style={{ marginTop: 12 }}>
          <Button type="dashed" icon={<PlusOutlined />} disabled={dataSource.length >= MAX_ITEMS} onClick={addRow}>
            {t('orders:orderDetail.addProduct')}
          </Button>
        </div>
      )}
      <Modal
        open={quantityConflicts.length > 0}
        title={t('orders:supplierOrders.detail.quantityChange.title')}
        okText={quantityDecision === 'TRANSPORT_PARTIAL'
          ? t('orders:supplierOrders.detail.quantityChange.openTransports')
          : t('orders:supplierOrders.detail.quantityChange.confirm')}
        cancelText={t('common:cancel')}
        onOk={confirmQuantityDecision}
        onCancel={closeQuantityDecision}
        confirmLoading={saving}
        width={720}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          {t('orders:supplierOrders.detail.quantityChange.description')}
        </Typography.Paragraph>

        <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 20 }}>
          {quantityConflicts.map((conflict) => (
            <div key={conflict.supplierOrderItemId}>
              <Typography.Text strong>{conflict.name}</Typography.Text>
              <div>
                <Typography.Text>
                  {conflict.currentQuantity} {formatUnit(conflict.unit)} → {conflict.nextQuantity} {formatUnit(conflict.unit)}
                </Typography.Text>
                <Typography.Text type="danger">
                  {' · '}{t('orders:supplierOrders.detail.quantityChange.missing', {
                    quantity: conflict.shortageQuantity,
                    unit: formatUnit(conflict.unit),
                  })}
                </Typography.Text>
              </div>
              {conflict.allocations.map((allocation) => (
                <div key={allocation.id || `${allocation.orderId}-${allocation.allocatedQuantity}`}>
                  <Typography.Text type="secondary" style={{ display: 'block' }}>
                    {allocation.orderNumber || allocation.orderId}{allocation.customerName ? ` · ${allocation.customerName}` : ''}: {' '}
                    {allocation.allocatedQuantity} {formatUnit(allocation.allocatedUnit || conflict.unit)} → {' '}
                    {allocation.proposedAllocatedQuantity} {formatUnit(allocation.allocatedUnit || conflict.unit)}
                  </Typography.Text>
                  {quantityDecision === 'CUSTOMER_REDUCTION'
                    && allocation.customerQuantity != null
                    && allocation.proposedCustomerQuantity != null && (
                    <Typography.Text type="secondary" style={{ display: 'block', paddingLeft: 16 }}>
                      {t('orders:supplierOrders.detail.quantityChange.customerOrderEffect')}: {' '}
                      {allocation.customerQuantity} {formatUnit(allocation.customerUnit || conflict.unit)} → {' '}
                      {allocation.proposedCustomerQuantity} {formatUnit(allocation.customerUnit || conflict.unit)}
                    </Typography.Text>
                  )}
                </div>
              ))}
            </div>
          ))}
        </Space>

        <Radio.Group value={quantityDecision} onChange={(event) => setQuantityDecision(event.target.value)} style={{ width: '100%' }}>
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            <Radio value="SUPPLIER_SHORTFALL">
              <Typography.Text strong>{t('orders:supplierOrders.detail.quantityChange.supplierShortfall')}</Typography.Text>
              <Typography.Text type="secondary" style={{ display: 'block', marginLeft: 24 }}>
                {t('orders:supplierOrders.detail.quantityChange.supplierShortfallHelp')}
              </Typography.Text>
            </Radio>
            <Radio value="CUSTOMER_REDUCTION">
              <Typography.Text strong>{t('orders:supplierOrders.detail.quantityChange.customerReduction')}</Typography.Text>
              <Typography.Text type="secondary" style={{ display: 'block', marginLeft: 24 }}>
                {t('orders:supplierOrders.detail.quantityChange.customerReductionHelp')}
              </Typography.Text>
            </Radio>
            <Radio value="TRANSPORT_PARTIAL">
              <Typography.Text strong>{t('orders:supplierOrders.detail.quantityChange.transportPartial')}</Typography.Text>
              <Typography.Text type="secondary" style={{ display: 'block', marginLeft: 24 }}>
                {t('orders:supplierOrders.detail.quantityChange.transportPartialHelp')}
              </Typography.Text>
            </Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </Card>
  );
};

export default SupplierOrderItemsSection;
